import type { ReadonlyDefaultMap } from "@glideapps/ts-necessities";
import { DefaultMap, definedMap, hasOwnProperty } from "@glideapps/ts-necessities";
import { setUnionInto } from "collection-utils";
import {
    asBoolean,
    asMaybeNumber,
    asNumber,
    asObjectValue,
    asString,
    detectedRecursiveComputation,
    isLoadedDefined,
    isValueNotEmpty,
    loadedDefinedMap,
    RecomputingFromOneValueProducer,
    RecomputingValueProducer,
} from "../common/components/component-helpers";
import type { BasePrimitiveValue } from "../common/components/computation-types";
import { Constant } from "../common/components/data";
import { registerDeserializable } from "../common/components/deserialization";
import { cleanNumber } from "../common/components/functions";
import type { Icon } from "../common/components/icons";
import { areValuesEqual } from "../common/components/primitives";
import type {
    AppDescription,
    AppKind,
    SerializedComponent,
    SerializedComponentsObject,
    UserFeatures,
} from "../common/components/SerializedApp";
import { defaultAppKind, getAppKind, getSourceMetadata, isNativeTable } from "../common/components/SerializedApp";
import type { QuotaBannerRequirements, UserProfileTableInfo } from "../common/components/types";
import type { Owner, UserData } from "../common/Database";
import type { TableName } from "../common/database-strings";
import type { BuilderAction, BuilderActionsForApp } from "../common/Database/builder-actions";
import type { EminenceFlags } from "../common/Database/eminence";
import { logError, logInfo } from "../common/debug-print";
import type {
    ActionComponentDescription,
    ActionDescription,
    ActionKind,
    AppUpdater,
    ArrayItemType,
    ClassOrArrayScreenDescription,
    ClassScreenDescription,
    ColumnAssignment,
    ColumnType,
    ColumnTypeKind,
    ComponentDescription,
    Description,
    FormScreenDescription,
    InputOutputTables,
    LegacyPropertyDescription,
    MutatingScreenKind,
    PropertyDescription,
    PropertySource,
    TableAndColumn,
    TableColumn,
    TableGlideType,
    TableRefGlideType,
    TypeSchema,
} from "../common/description";
import { findTable, getPrimitiveColumns, PropertyKind, sheetNameForTable } from "../common/description";
import { getDocURL } from "../common/docUrl";
import { addUSD, formatNumberFixed, makeFixedNumberFormatter } from "../common/format-number";
import type { CompoundActionNode } from "../common/generator/actions/compound";
import { defined, definedMapWithDefault, isArray, mapRecord, panic } from "../support";
import type { PropertySection, SuperPropertySection } from "./property-sections";
import { getActionProperty, makeActionProperty } from "./property-utils";
import { fetchLinkColumn, SerializedElementBuilder } from "./serialized-component";

// title, subtitle, image, link
export type Summary = [
    SerializedComponent | undefined,
    SerializedComponent | undefined,
    SerializedComponent | undefined,
    SerializedComponent | undefined
];

export interface AllowedDataEdits {
    readonly addRowToTable: ReadonlyDefaultMap<TableGlideType, ReadonlySet<string>>;
    readonly setColumnsInRow: ReadonlyDefaultMap<TableGlideType, ReadonlySet<string>>;
    readonly deleteRow: ReadonlySet<TableGlideType>;
}

export interface MutableAllowedDataEdits extends AllowedDataEdits {
    readonly addRowToTable: DefaultMap<TableGlideType, Set<string>>;
    readonly setColumnsInRow: DefaultMap<TableGlideType, Set<string>>;
    readonly deleteRow: Set<TableGlideType>;
}

export function makeEmptyMutableAllowedDataEdits(): MutableAllowedDataEdits {
    return {
        addRowToTable: new DefaultMap(() => new Set()),
        setColumnsInRow: new DefaultMap(() => new Set()),
        deleteRow: new Set(),
    };
}

export function mergeAllowedDataEdits(dst: MutableAllowedDataEdits, src: AllowedDataEdits | undefined): void {
    if (src === undefined) return;

    for (const [k, v] of src.addRowToTable) {
        setUnionInto(dst.addRowToTable.get(k), v);
    }
    for (const [k, v] of src.setColumnsInRow) {
        setUnionInto(dst.setColumnsInRow.get(k), v);
    }
    setUnionInto(dst.deleteRow, src.deleteRow);
}

export function convertEditedColumnsToAllowedDataEdits(
    editedColumns: EditedColumns,
    schema: SchemaInspector
): MutableAllowedDataEdits {
    const edits = makeEmptyMutableAllowedDataEdits();
    for (const [cn, , isAddRow, tn] of editedColumns) {
        const t = schema.findTable(tn);
        if (t === undefined) continue;

        if (isAddRow) {
            edits.addRowToTable.get(t).add(cn);
        } else {
            edits.setColumnsInRow.get(t).add(cn);
        }
    }
    return edits;
}

export function convertEditedColumnsToIndirect(editedColumns: EditedColumns): EditedColumns {
    return editedColumns.map(([cn, , isAddRow, tn]) => [cn, false, isAddRow, tn]);
}

export interface UserProfileRowBuild {
    readonly row: SerializedComponent;
    readonly table: TableGlideType;
}

export interface ValueForColumn {
    readonly value: SerializedComponent;
    readonly table: TableGlideType | undefined;
    readonly column: TableColumn | undefined;
}

export interface FetchingBuilder extends SchemaInspector {
    readonly b: SerializedElementBuilder;
    readonly isBuilder: boolean;

    info(...args: readonly any[]): void;
    error(...args: readonly any[]): void;
    fetchTableRows(tableName: TableName, forDataEditor: boolean): SerializedComponent;
    fetchLinkColumn(column: TableColumn, columnCtx: SerializedComponent, forDataEditor: boolean): SerializedComponent;
    registerTableUsed(tableName: TableName): void;

    makeUserProfileRow(): UserProfileRowBuild | undefined;

    // We will never try to compile to JS if this always return `false`
    getCompiledJSCode(name: string): SerializedComponent | undefined | false;
    setCompiledJSFailed?(name: string): void;
    setCompiledJavaScript?(name: string, code: string, serializedComponent: SerializedComponent): void;
}

export const builderJSCompiledFormulas = new Map<string, SerializedComponent | false>();

export const compiledJSCallbacks = {
    asObjectValue,
    definedMap,
    isValueNotEmpty,
    isLoadedDefined,
    loadedDefinedMap,
    RecomputingValueProducer,
    RecomputingFromOneValueProducer,
    asBoolean,
    asString,
    asNumber,
    areValuesEqual,
    registerDeserializable,
    asMaybeNumber,
    cleanNumber,
    formatNumberFixed,
    Constant,
    makeFixedNumberFormatter,
    addUSD,
    detectedRecursiveComputation,
};

export type CompiledJSCallbacks = Record<keyof typeof compiledJSCallbacks, unknown>;

export class SimpleFetchingBuilder implements FetchingBuilder {
    public readonly b: SerializedElementBuilder = new SerializedElementBuilder();

    constructor(
        public readonly schema: TypeSchema,
        public readonly userProfileTableInfo: UserProfileTableInfo | undefined,
        public readonly isBuilder: boolean
    ) {}

    public findTable(ref: TableName | TableRefGlideType | undefined): TableGlideType | undefined {
        return findTable(this.schema, ref);
    }

    // FIXME: Why do we take `forBuilder` here when we have it?
    public fetchTableRows(tableName: TableName, forBuilder: boolean): SerializedComponent {
        return this.b.fetchTableRows("default", tableName.name, forBuilder);
    }

    // FIXME: Why do we take `forBuilder` here when we have it?
    public fetchLinkColumn(
        column: TableColumn,
        columnCtx: SerializedComponent,
        forBuilder: boolean
    ): SerializedComponent {
        return fetchLinkColumn(this.b, column, columnCtx, forBuilder);
    }

    public registerTableUsed(_tableName: TableName): void {
        return;
    }

    public info(...args: readonly any[]): void {
        logInfo(...args);
    }

    public error(...args: readonly any[]): void {
        logError(...args);
    }

    public makeUserProfileRow(): UserProfileRowBuild | undefined {
        const tableName = this.userProfileTableInfo?.tableName;
        if (tableName === undefined) return undefined;

        const table = this.findTable(tableName);
        if (table === undefined) return undefined;

        return {
            row: this.b.userProfileRow,
            table,
        };
    }

    public getCompiledJSCode(_name: string): SerializedComponent | undefined | false {
        return false;
    }
}

export class SimpleFetchingEvaluatingBuilder extends SimpleFetchingBuilder {
    public getCompiledJSCode(name: string): SerializedComponent | undefined | false {
        return builderJSCompiledFormulas.get(name);
    }

    public setCompiledJSFailed(name: string): void {
        builderJSCompiledFormulas.set(name, false);
    }

    public setCompiledJavaScript(name: string, code: string, serializedComponent: SerializedComponent): void {
        builderJSCompiledFormulas.set(name, serializedComponent);
        // eslint-disable-next-line no-eval
        eval(code)(compiledJSCallbacks);
    }
}

export interface ComponentBuilder extends FetchingBuilder, AppDescriptionContext {
    makeComponents(
        uniqueIDPrefix: string,
        componentDescriptions: readonly ComponentDescription[],
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        // This must not depend on the context, i.e. should be a `getVariable`
        outputContext: SerializedComponent,
        searchVariable: SerializedComponent | undefined,
        searchNotActive: SerializedComponent | undefined,
        visibilityConditions: readonly (SerializedComponent | undefined)[],
        validationVariables: readonly (SerializedComponent | undefined)[]
    ): { components: readonly SerializedComponent[]; quotaRequirements: QuotaBannerRequirements };
    makeVisibilityConditions(
        table: TableGlideType,
        components: readonly ComponentDescription[],
        context: SerializedComponent
    ): readonly (SerializedComponent | undefined)[];
    makeColumnValues(
        tables: InputOutputTables,
        assignments: readonly ColumnAssignment[],
        alreadyAssigned: ReadonlySet<string> | undefined,
        defaultValues: readonly [string, SerializedComponent][],
        screenContext: SerializedComponent | undefined,
        undefinedIfEmpty: boolean
    ): SerializedComponentsObject;
    makePushScreen(
        screenNameOrType: string | ColumnType,
        title: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined,
        screenData: SerializedComponent
    ): SerializedComponent | undefined;
    makePushAddItemScreen(itemType: ArrayItemType, arrayData: SerializedComponent): SerializedComponent | undefined;
    makePushEditItemScreen(
        itemType: TableRefGlideType,
        itemData: SerializedComponent,
        title: SerializedComponent | undefined
    ): SerializedComponent | undefined;
    makePushFormScreen(
        screenName: string,
        title: SerializedComponent,
        screenContext: SerializedComponent
    ): SerializedComponent | undefined;
    makePushFreeScreen(
        screenName: string,
        title: string | SerializedComponent | undefined
    ): SerializedComponent | undefined;
    makePushMenuScreen(
        menuID: string,
        primaryKey: SerializedComponent,
        defaultScreenName: string,
        title: SerializedComponent | undefined,
        screenContext: SerializedComponent
    ): SerializedComponent | undefined;
    makeSequenceFromActions(
        descs: ReadonlyArray<ActionDescription>,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        fromArrayScreen: boolean
    ): SerializedComponent | undefined;
    makePushFilterScreen(
        items: SerializedComponent | undefined,
        values: SerializedComponent | undefined,
        filterColumn: SerializedComponent | undefined,
        sortProperties: SerializedComponent | undefined,
        sortProperty: SerializedComponent | undefined,
        sortAscending: SerializedComponent | undefined
    ): SerializedComponent;
    makePushOverlayEditorScreen(
        value: SerializedComponent,
        title: SerializedComponent,
        placeholder: SerializedComponent,
        doneText: SerializedComponent,
        doneIcon: SerializedComponent,
        doneStyle: SerializedComponent,
        allowEmpty: boolean,
        onChange: SerializedComponent
    ): SerializedComponent;
    makePushChoiceScreen(
        value: SerializedComponent,
        choices: SerializedComponent,
        title: SerializedComponent | undefined,
        isRequired: SerializedComponent,
        isMulti: boolean,
        onChange: SerializedComponent | undefined
    ): SerializedComponent;
    makePushSignaturePadScreen(
        message: SerializedComponent,
        signatureURL: SerializedComponent,
        onChange: SerializedComponent | undefined,
        validFlag: SerializedComponent | undefined
    ): SerializedComponent;
    makePushCodeScannerScreen(
        standards: SerializedComponent,
        acceptOnFirst: SerializedComponent,
        output: SerializedComponent,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent;
    makePushLiveLinearCodeScannerScreen(
        output: SerializedComponent,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent;
    makePushEventPickerScreen(
        startValue: SerializedComponent,
        endValue: SerializedComponent,
        events: SerializedComponent | undefined,
        title: SerializedComponent,
        isRequired: SerializedComponent,
        onChange: SerializedComponent | undefined
    ): SerializedComponent;
    makePushSendAppFeedbackScreen(): SerializedComponent;
    classScreenDescriptionForTable(t: TableRefGlideType): ClassScreenDescription | undefined;
}

export function updatesForUntypedProperty<T extends Description>(
    source: PropertySource,
    value: unknown | undefined
): Partial<T> {
    if (hasOwnProperty(source, "name")) {
        const updates: { [p: string]: PropertyDescription | undefined } = {};
        updates[source.name] = value as any;
        return updates as any;
    } else {
        return panic("Untyped updates not supported for indirect setters");
    }
}

export function updatesForProperty<T extends Description>(
    source: PropertySource,
    desc: Description,
    value: PropertyDescription | undefined,
    updater: AppUpdater | undefined
): Partial<T> {
    if (hasOwnProperty(source, "name")) {
        return updatesForUntypedProperty(source, value);
    } else {
        return source.update(desc, value, updater);
    }
}

export function updatesForPropertyValue<P, T extends Description>(
    kind: PropertyKind,
    source: PropertySource,
    desc: Description,
    value: P | undefined,
    updater: AppUpdater | undefined
): Partial<T> {
    return updatesForProperty(
        source,
        desc,
        definedMap(value, v => ({ kind, value: v })),
        updater
    );
}

export function isNamedPropertySource(source: PropertySource): source is { readonly name: string } {
    return hasOwnProperty(source, "name");
}

export function getPropertyDescription<P>(
    desc: Description,
    descr: PropertyDescriptor
): PropertyDescription | undefined {
    const source = descr.property;
    let value: LegacyPropertyDescription | undefined;
    if (isNamedPropertySource(source)) {
        value = (desc as any)[source.name] ?? source.defaultValue;
    } else {
        value = source.get(desc);
    }
    if (value === undefined) return undefined;

    const pdc = isMultiCasePropertyDescriptor(descr) ? defined(descr.cases[0]) : descr;
    // FIXME: Move this into description handler
    if (pdc.kind === PropertyKind.Formula || pdc.kind === PropertyKind.Transforms || pdc.kind === PropertyKind.Sorts) {
        // `P` kinda has to be `Formula` here.
        return { kind: pdc.kind, value: value as P };
    }
    if (typeof value === "object" && hasOwnProperty(value, "kind")) {
        return value as PropertyDescription;
    }
    if (pdc.kind === PropertyKind.Filter) {
        if (!hasOwnProperty(value, "operator")) {
            return panic("Filter property description is invalid");
        }
    } else if (pdc.kind === PropertyKind.Action) {
        if (isArray(value) && value.length === 0) return undefined;
        value = getActionProperty(value);
        if (value === undefined) {
            logError("Action property description is invalid", value);
            return undefined;
        }
    } else if (typeof value !== "string" && typeof value !== "boolean") {
        logError("Property description is neither string nor boolean", value);
        return undefined;
    }
    return { kind: pdc.kind, value };
}

export function getPropertyDescriptionValue<P>(desc: Description, descr: PropertyDescriptor): P | undefined;
export function getPropertyDescriptionValue<P, U>(
    desc: Description,
    descr: PropertyDescriptor,
    f: (v: LegacyPropertyDescription) => U | undefined
): U | undefined;
export function getPropertyDescriptionValue<P, U>(
    desc: Description,
    descr: PropertyDescriptor,
    f?: (v: LegacyPropertyDescription) => U | undefined
): P | U | undefined {
    const pd = getPropertyDescription<P>(desc, descr);
    if (pd === undefined) return undefined;
    if (f === undefined) return pd.value as P;
    return f(pd);
}

export type PropertyLabel = string | ((i: number | undefined) => string);

export interface Subcomponent {
    readonly name: string;
    readonly important: boolean;
    readonly order?: number; // defaults to 0
}

interface PropertyDescriptorBase {
    readonly property: PropertySource;
    readonly label: PropertyLabel;
    readonly accessoryTo?: PropertyDescriptorBase;
    readonly section: SuperPropertySection;
    readonly subcomponent?: Subcomponent;
    readonly writesToColumn?: TableAndColumn;
    // All configurators will be sorted within their section.  The ones with a
    // position come first, sorted by the position.  After that come all the
    // rest.
    readonly position?: number;
    readonly isSearchable?: boolean; // defaults to `true`
}

interface ConfigurablePropertyDescriptor extends PropertyDescriptorBase {
    // This must only be set to `true` if the property value doesn't actually
    // reside in the corresponding description, and will be rewritten at its
    // actual location, such as the source sheet for the Show New Screen action.
    readonly doNotRewrite?: boolean;
}

export type ColumnTypePredicate = ((t: ColumnType) => boolean) | ColumnTypeKind;

export type GetAllowedColumnsFunction = (
    table: TableGlideType,
    desc: Description,
    schema: SchemaInspector
) => readonly TableColumn[];

interface IndirectTableGetter {
    getIndirectTable?(
        tables: InputOutputTables,
        desc: Description,
        schema: SchemaInspector
    ): TableGlideType | undefined;
}

export interface PropertyDescriptorCase {
    readonly kind: PropertyKind;
}

export interface ColumnPropertyDescriptorCase extends PropertyDescriptorCase, IndirectTableGetter {
    readonly kind: PropertyKind.Column;

    readonly required: boolean;
    readonly editable: boolean;
    readonly searchable: boolean;
    readonly emptyByDefault?: boolean; // defaults to `false`
    readonly isDefaultCaption?: boolean;
    readonly isLegacy?: boolean;
    readonly isEditedInApp?: boolean;
    readonly applyFormat?: boolean;
    // The value must be already present, i.e. binding this to an output
    // column without a pre-existing input is invalid.  We use this for
    // the default value properties.
    readonly needsExistingValue?: boolean;
    readonly allowUserProfileColumns?: boolean;
    getAllowedColumns: GetAllowedColumnsFunction;
    readonly preferredNames?: ReadonlyArray<string>;
    // This one is currently only used for the caption property, to discourage
    // Glide picking the same property as it picked for title or description.
    // I think this should go away, however, and we should by default not pick
    // a column that's used for another property.
    readonly ignoreColumnsFromProperties?: readonly string[];
    readonly preferredType?: ColumnTypePredicate;
    // When rewriting with `RewriteWithNewTable`, try to assign a column even
    // if not required.
    readonly invalidateForNewTable?: boolean;
    getEmptyCase?(tables: InputOutputTables): { label: string; linkURL: string };
}

export interface ColumnPropertyDescriptor extends ConfigurablePropertyDescriptor, ColumnPropertyDescriptorCase {}

export interface TablePropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Table;

    readonly required: boolean;
    readonly isDefaultCaption?: boolean;
    getAllowedTables(
        desc: Description,
        schema: TypeSchema,
        componentTables: InputOutputTables | undefined
    ): ReadonlyArray<TableGlideType>;
}

export interface TablePropertyDescriptor extends ConfigurablePropertyDescriptor, TablePropertyDescriptorCase {}

export interface EnumPropertyCase<P extends BasePrimitiveValue> {
    readonly value: P;
    readonly label: string;
    readonly icon?: string;

    // This is currently used by the Favorites overlay button to indicate that
    // it's writing to the "Is Favorited?" column.
    readonly columnWritten?: string;
}

export type EnumVisual = "dropdown" | "small-images" | "large-images" | "slider" | "radio";

// `getIndirectTable` is only used if some of the cases have a `columnWritten`.
export interface EnumPropertyDescriptorCase<P extends BasePrimitiveValue>
    extends PropertyDescriptorCase,
        IndirectTableGetter {
    readonly kind: PropertyKind.Enum;

    readonly menuLabel: string;
    readonly cases: readonly EnumPropertyCase<P>[];
    // If this is specified, the configurator will work even if
    // the value is `undefined`.
    readonly defaultCaseValue?: P;
    readonly visual: EnumVisual;
    // Should be `true` if the value of this enum influences anything
    // else about the component descriptor.  The list layout in the
    // Inline List is an example.
    readonly changesDescriptor?: boolean;
}

export interface EnumPropertyDescriptor<P extends BasePrimitiveValue>
    extends ConfigurablePropertyDescriptor,
        EnumPropertyDescriptorCase<P> {}

export interface StringPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.String;

    readonly required: boolean;
    readonly placeholder: string;
    readonly isImageURL?: boolean;
    // `undefined` means it's not the caption.  A string means
    // it's the caption, and that string is the default caption.
    readonly isCaption?: string;
    readonly isMultiLine?: boolean;
    readonly menuLabel?: string;
    readonly defaultValue?: string;
    readonly syntaxMode?: string;
}

export interface StringPropertyDescriptor extends ConfigurablePropertyDescriptor, StringPropertyDescriptorCase {}

export enum NumberPropertyStyle {
    Entry,
    Stepper,
}

export enum RequiredKind {
    Required,
    NotRequiredDefaultPresent,
    NotRequiredDefaultMissing,
}

export interface NumberPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Number;

    readonly required: RequiredKind;
    readonly placeholder: string;
    readonly style?: NumberPropertyStyle;
    readonly defaultValue: number;
}

export interface NumberPropertyDescriptor extends ConfigurablePropertyDescriptor, NumberPropertyDescriptorCase {}

export interface SwitchPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Switch;

    readonly defaultValue?: boolean;
}

export interface SwitchPropertyDescriptor extends ConfigurablePropertyDescriptor, SwitchPropertyDescriptorCase {}

export interface ConstantPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Constant;

    readonly value: string;
}

interface ConstantPropertyDescriptor extends PropertyDescriptorBase, ConstantPropertyDescriptorCase {
    readonly doNotRewrite: true;
}

export interface SpecialValuePropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.SpecialValue;
    readonly withActionSource: boolean;
    readonly withClearColumn: boolean;
}

interface SpecialValuePropertyDescriptor extends ConfigurablePropertyDescriptor, SpecialValuePropertyDescriptorCase {}

export interface PaymentMethodPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.PaymentMethod;
}

interface PaymentMethodPropertyDescriptor extends ConfigurablePropertyDescriptor, PaymentMethodPropertyDescriptorCase {}

export interface ScreenPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Screen;
}

interface ScreenPropertyDescriptor extends ConfigurablePropertyDescriptor, ScreenPropertyDescriptorCase {}

export interface FilterPropertyDescriptorCase extends PropertyDescriptorCase, IndirectTableGetter {
    readonly kind: PropertyKind.Filter;
}

export function getFilterTable(
    descr: FilterPropertyDescriptorCase,
    tables: InputOutputTables,
    desc: Description,
    schema: SchemaInspector
): TableGlideType | undefined {
    return definedMapWithDefault(descr.getIndirectTable, tables.input, f => f(tables, desc, schema));
}

interface FilterPropertyDescriptor extends ConfigurablePropertyDescriptor, FilterPropertyDescriptorCase {}

enum FormulaTemplate {
    FilterReference = "filter-reference",
    TextTemplate = "text-template",
    Predicate = "predicate",
}

export interface FormulaPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Formula;

    readonly template: FormulaTemplate;
}

interface FormulaPropertyDescriptor extends ConfigurablePropertyDescriptor, FormulaPropertyDescriptorCase {}

export interface TransformsPropertyDescriptorCase extends PropertyDescriptorCase, IndirectTableGetter {
    readonly kind: PropertyKind.Transforms;

    addText: string;
    description: string;
    allowContextTable: boolean;
    allowLHSUserProfileColumns: boolean;
    specPrefix?: string;
    withContainingScreen: boolean;
}

interface TransformsPropertyDescriptor extends ConfigurablePropertyDescriptor, TransformsPropertyDescriptorCase {}

export interface SortsPropertyDescriptorCase extends PropertyDescriptorCase, IndirectTableGetter {
    readonly kind: PropertyKind.Sorts;
}

interface SortsPropertyDescriptor extends ConfigurablePropertyDescriptor, SortsPropertyDescriptorCase {}

export enum ArrayPropertyStyle {
    Default,
    // For these two, `properties` must have two items: first the
    // key/caption/title, then the value.
    KeyValue,
    ActionArray,
    Card,
}

export interface ArrayPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Array;

    readonly properties: readonly PropertyDescriptor[];
    readonly allowEmpty: boolean;
    readonly addItemLabels: readonly string[];
    readonly maxItems?: number;
    readonly specialItems?: number;
    readonly defaultValue?: boolean | readonly Description[];
    readonly allowReorder: boolean;
    readonly style: ArrayPropertyStyle;
}

interface ArrayPropertyDescriptor extends ConfigurablePropertyDescriptor, ArrayPropertyDescriptorCase {}

export interface ZapPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Zap;
}

export interface ZapPropertyDescriptor extends ConfigurablePropertyDescriptor, ZapPropertyDescriptorCase {}

export interface WebhookPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Webhook;
}

export interface WebhookPropertyDescriptor extends ConfigurablePropertyDescriptor, WebhookPropertyDescriptorCase {}

export interface IconPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Icon;

    readonly defaultIcon?: Icon;
}

export interface IconPropertyDescriptor extends ConfigurablePropertyDescriptor, IconPropertyDescriptorCase {}

export interface EmojiPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.Emoji;

    readonly defaultEmoji?: string;
}

export interface EmojiPropertyDescriptor extends ConfigurablePropertyDescriptor, EmojiPropertyDescriptorCase {}

export interface ActionPropertyDescriptorCase extends PropertyDescriptorCase, IndirectTableGetter {
    readonly kind: PropertyKind.Action;

    readonly kinds: readonly ActionKind[];
    readonly defaultAction?: boolean | ((table: TableGlideType, desc: Description) => ActionDescription | undefined);
    // This is only used for ##inlineListDefaultAction.
    readonly defaultActionForUndefined?: boolean;
    readonly required?: boolean;
}

export interface ActionPropertyDescriptor extends ConfigurablePropertyDescriptor, ActionPropertyDescriptorCase {}

export interface CompoundActionPropertyDescriptorCase extends PropertyDescriptorCase {
    readonly kind: PropertyKind.CompoundAction;
}

interface CompoundActionPropertyDescriptor
    extends ConfigurablePropertyDescriptor,
        CompoundActionPropertyDescriptorCase {}

export function getTransformsTable(
    descr: TransformsPropertyDescriptorCase | SortsPropertyDescriptorCase,
    tables: InputOutputTables,
    desc: Description,
    schema: SchemaInspector
): TableGlideType | undefined {
    return definedMapWithDefault(descr.getIndirectTable, tables.input, f => f(tables, desc, schema));
}

export function shouldUseInputContextForTransform(mutatingScreenKind: MutatingScreenKind | undefined): boolean {
    // In a regular detail screen, the containing screen context is
    // the screen's context.  In mutating screens we have the input
    // and the output context.  We pick the output context as the
    // containing screen context.  Ideally we'd support both.
    return mutatingScreenKind === undefined;
}

export interface MultiCasePropertyDescriptor extends ConfigurablePropertyDescriptor {
    readonly cases: ReadonlyArray<PropertyDescriptorCase>;
}

export function isMultiCasePropertyDescriptor(descr: PropertyDescriptor): descr is MultiCasePropertyDescriptor {
    return (descr as any).kind === undefined;
}

export type PropertyDescriptor =
    | ColumnPropertyDescriptor
    | TablePropertyDescriptor
    | EnumPropertyDescriptor<any>
    | StringPropertyDescriptor
    | NumberPropertyDescriptor
    | SwitchPropertyDescriptor
    | ConstantPropertyDescriptor
    | MultiCasePropertyDescriptor
    | SpecialValuePropertyDescriptor
    | PaymentMethodPropertyDescriptor
    | ScreenPropertyDescriptor
    | FilterPropertyDescriptor
    | FormulaPropertyDescriptor
    | TransformsPropertyDescriptor
    | SortsPropertyDescriptor
    | ArrayPropertyDescriptor
    | ZapPropertyDescriptor
    | WebhookPropertyDescriptor
    | IconPropertyDescriptor
    | EmojiPropertyDescriptor
    | ActionPropertyDescriptor
    | CompoundActionPropertyDescriptor;

export type PropertyTableGetter = (
    componentTables: InputOutputTables,
    desc: Description,
    schema: SchemaInspector
) => TableGlideType | undefined;

// "optional" is ignored.  It's only here to do `flag ? "required" : "optional"`
export type StringyColumnPropertyFlag =
    | "required"
    | "optional"
    | "editable"
    | "searchable"
    | "edited-in-app"
    | "empty-by-default";

export function makeStringyColumnPropertyDescriptor(
    name: string,
    label: string,
    flags: readonly StringyColumnPropertyFlag[],
    section: PropertySection,
    getPropertyTable: PropertyTableGetter | undefined,
    preferredNames: ReadonlyArray<string> | undefined,
    ignoreColumnsFromProperties: readonly string[] = [],
    preferredType: ColumnTypePredicate = "string"
): PropertyDescriptor {
    const required = flags.indexOf("required") >= 0;
    const editable = flags.indexOf("editable") >= 0;
    const searchable = flags.indexOf("searchable") >= 0;
    const isEditedInApp = flags.indexOf("edited-in-app") >= 0;
    const emptyByDefault = flags.indexOf("empty-by-default") >= 0;
    return {
        kind: PropertyKind.Column,
        property: { name },
        label,
        required,
        editable,
        searchable,
        isEditedInApp,
        emptyByDefault,
        getIndirectTable: getPropertyTable,
        getAllowedColumns: getPrimitiveColumns,
        preferredNames,
        ignoreColumnsFromProperties,
        preferredType,
        section,
    };
}

// FIXME: This and `updateForActionDescriptions` have to go away.  We shouldn't
// have a pre-defined `actions` property anymore.  All actions should just be
// regular properties.
const actionsPropertyName = "actions";

export function updateForActionDescriptions<T extends Description>(
    actions: ReadonlyArray<ActionDescription>
): Partial<T> {
    const updates: Partial<T> = {};
    (updates as any)[actionsPropertyName] = actions;
    return updates;
}

// All actions other than the ones in `actions` are regular properties.
export function wrapActionProperties(actions: ActionsRecord): Partial<ActionComponentDescription> {
    return mapRecord(actions, (v, k) => {
        if (k === actionsPropertyName) return v;
        if (v.length === 0) return undefined;
        return makeActionProperty(v[0]);
    });
}

export interface ComponentDescriptor {
    readonly name: string;
    readonly description: string;
    readonly img: string;
    readonly group: string;
    readonly isLegacy?: boolean;
    readonly isHidden?: boolean;
    readonly isPro?: boolean;
    readonly needsInputContext?: boolean; // default is `false`
    readonly properties: ReadonlyArray<PropertyDescriptor>;
    readonly helpUrl: string;
    readonly specialCaseDescriptor?: ComponentSpecialCaseDescriptor;
    readonly quotaWarning?: string;
    // Does this component let the user also configure the screen title?
    readonly configuresScreenTitle?: boolean; // defaults to `false`
}

export interface SchemaInspector {
    readonly schema: TypeSchema;
    readonly userProfileTableInfo: UserProfileTableInfo | undefined;

    findTable(ref: TableName | TableRefGlideType | undefined): TableGlideType | undefined;
}

export interface AppDescriptionContext extends SchemaInspector {
    readonly appID: string;
    readonly appKind: AppKind;
    readonly appDescription: AppDescription | undefined;
    readonly builderActions: BuilderActionsForApp;
    readonly userFeatures: UserFeatures;
    readonly eminenceFlags: EminenceFlags;
    getBuilderAction(id: string): BuilderAction | undefined;
}

export function isNativeTableInAppDescriptionContext(adc: AppDescriptionContext, tableName: TableName): boolean {
    return definedMap(adc.appDescription, a => isNativeTable(getSourceMetadata(a), tableName)) ?? false;
}

export function getAppKindFromAppDescriptionContext(adc: AppDescriptionContext): AppKind {
    return definedMap(adc.appDescription, getAppKind) ?? defaultAppKind;
}

export interface ExistingAppDescriptionContext extends AppDescriptionContext {
    readonly appDescription: AppDescription;
}

export interface RewritingComponentConfiguratorContext extends AppDescriptionContext {
    // Returns whether the screen was added successfully/is available
    requireScreen(screenName: string): boolean;
    requireMenuScreen(menuID: string): boolean;
}

export interface PasteboardState {
    readonly components: readonly ComponentDescription[];
    readonly appKind: AppKind;
}

export interface InteractiveComponentConfiguratorContext extends RewritingComponentConfiguratorContext, AppUpdater {
    readonly appDescription: AppDescription;
    readonly user: UserData | undefined;
    readonly owner: Owner | undefined;
    readonly pasteboard: PasteboardState;
    readonly isInteractiveComponentConfiguratorContext: true;

    addFormScreen(screensCreated: Set<string>, makeScreen: () => FormScreenDescription): string;
    addFreeScreen(screensCreated: Set<string>, makeScreen: () => ClassOrArrayScreenDescription): string;
    addFreeScreenFromTable(table: TableGlideType, isRootScreen: boolean): string;

    makeAction(
        actionKind: ActionKind,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescription | undefined;
    // This will also add the form screen to the app description.
    makeFormActionForTables(tables: InputOutputTables): ActionDescription | undefined;

    saveBuilderAction(
        actionID: string | undefined,
        tableName: TableName,
        title: string | undefined,
        description: string | undefined,
        action: CompoundActionNode
    ): Promise<string>;
    makeDefaultCompoundAction(
        table: TableGlideType,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): Promise<string>;

    isComponentNew(descriptor: ComponentDescriptor | ComponentSpecialCaseDescriptor): boolean;

    upgradeAppToPro(source: string, setProAsDefault: boolean): void;

    pushUndoState(): void;
}

export function promoteAppDescriptionContext(
    adc: AppDescriptionContext
): InteractiveComponentConfiguratorContext | undefined {
    if (!hasOwnProperty(adc, "isInteractiveComponentConfiguratorContext")) return undefined;
    if (adc.isInteractiveComponentConfiguratorContext !== true) return undefined;
    return adc as InteractiveComponentConfiguratorContext;
}

export interface ComponentErrorAndLink {
    readonly errorMessage: string;
    readonly linkMessage: string;
    readonly linkURL: string;
}

export interface ComponentSpecialCaseDescriptor {
    readonly name: string;
    readonly analyticsName?: string;
    readonly description: string;
    readonly img?: string;
    readonly group: string;
    readonly isNew?: boolean;
    readonly isPro?: boolean;
    readonly needsInputContext?: boolean;
    readonly isLegacy?: boolean;
    readonly appKinds: AppKind | "both";
}

export enum ComponentPrerequisite {
    StripeConnect,
}

export interface ActionsRecord {
    readonly actions: readonly ActionDescription[];
    readonly [name: string]: readonly ActionDescription[];
}

export function mapActionsRecord(
    actions: ActionsRecord,
    f: (a: readonly ActionDescription[]) => readonly ActionDescription[]
): ActionsRecord {
    return mapRecord(actions, f) as ActionsRecord;
}

// [column name, edits in screen context (vs in indirect table), is Add Row, table name]
export type EditedColumn = readonly [
    columnName: string,
    editsInScreenContext: boolean,
    isAddRow: boolean,
    tableName: TableName
];
export type EditedColumns = readonly EditedColumn[];

export interface EditedColumnsAndTables {
    readonly editedColumns: EditedColumns;
    readonly deletedTables: readonly TableName[];
}

export const emptyEditedColumnsAndTables: EditedColumnsAndTables = { editedColumns: [], deletedTables: [] };

export function combineEditedColumnsAndTables(
    e1: EditedColumnsAndTables,
    e2: EditedColumnsAndTables | undefined
): EditedColumnsAndTables {
    if (e2 === undefined) return e1;
    return {
        editedColumns: [...e1.editedColumns, ...e2.editedColumns],
        deletedTables: [...e1.deletedTables, ...e2.deletedTables],
    };
}

export function componentErrorAndLinkForReference(
    table: TableGlideType,
    withMultiRelations: boolean
): ComponentErrorAndLink {
    return {
        errorMessage: `The "${sheetNameForTable(table)}" sheet doesn't have a ${
            withMultiRelations ? "" : "single-"
        }relation column.`,
        linkMessage: "Learn how to add one",
        linkURL: getDocURL("addRelationColumn"),
    };
}
